// src/components/MusicRadioForm.jsx
import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

/**
 * MusicRadioForm
 *
 * Form to add GTA music & radio entries to Firestore (collection: radio_stations).
 * Mirrors MissionForm UX patterns (arrays, markdown previews, validation banners).
 *
 * Expected document shape (as consumed by your MusicRadioIndex list page):
 * {
 *   slug: string | null,
 *   name: string,               // required
 *   tagline: string,
 *   frequency: string,          // e.g., "102.7 FM" or "Streaming"
 *   city: string,               // e.g., "Vice City" / "Los Santos"
 *   djs: string[],
 *   genres: string[],
 *   coverImage: string | null,  // URL
 *   gallery: string[],          // URL[]
 *   spotifyUrl: string | null,  // https://open.spotify.com/embed/... preferred
 *   youtubeUrl: string | null,
 *   appleMusicUrl: string | null,
 *   description: string,        // markdown
 *   trivia: string,             // markdown (optional)
 *   tags: string[],
 *   reactions: object,          // {}
 *   createdBy: string,          // user.uid
 *   createdAt: serverTimestamp,
 *   updatedAt: serverTimestamp,
 *   status: "published" | "pending" | "draft"
 * }
 */

const DEFAULT_CITIES = ["Vice City", "Los Santos", "Liberty City", "Blaine County", "Other"];
const SAMPLE_STATION = {
  name: "Radio Los Santos",
  slug: "radio-los-santos",
  tagline: "Modern hip-hop and street anthems.",
  game: "GTA V",
  frequency: "RLS",
  city: "Los Santos",
  djs: ["Big Boy"],
  genres: ["Hip-Hop", "Rap"],
  coverImage:
    "https://images.unsplash.com/photo-1511140973288-0192b9f02cd4?q=80&w=1600",
  gallery: [
    "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?q=80&w=1600",
  ],
  spotifyUrl:
    "https://open.spotify.com/embed/playlist/37i9dQZF1DX9PZLL0lfjZK",
  youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  appleMusicUrl: "",
  description: `### About
Iconic Los Santos station featuring modern hip-hop and guest celebrity DJs.`,
  trivia: `- First appearance in GTA V.
- DJ: Big Boy.`,
  tags: ["gta-v", "los-santos", "hip-hop"],
  status: "published",
};

const GAME_OPTIONS = ["GTA VI", "GTA V", "GTA IV", "San Andreas", "Vice City", "GTA III", "Others"];

export default function MusicRadioForm({
  user,
  enforceAdmin = false,
  isAdmin = false,
  onSaved,
  onCancel,
  initial = {},
  extraMeta = {},
}) {
  const isAuthed = Boolean(user?.uid);
  const blockedByAdminGuard = enforceAdmin && (!isAuthed || !isAdmin);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    tagline: "",
    frequency: "",
    city: "",
    game: initial.game || "GTA VI",
    djs: [""],
    genres: [""],
    coverImage: "",
    gallery: [""],
    spotifyUrl: "",
    youtubeUrl: "",
    appleMusicUrl: "",
    description: "",
    trivia: "",
    tagsCsv: "",
    status: "published",
    ...initial,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [previews, setPreviews] = useState({ description: false, trivia: false });

  const updateField = (name, value) => setForm((s) => ({ ...s, [name]: value }));
  const updateArrayField = (arrKey, idx, value) =>
    setForm((s) => {
      const arr = [...s[arrKey]];
      arr[idx] = value;
      return { ...s, [arrKey]: arr };
    });
  const addArrayRow = (arrKey, blank = "") => setForm((s) => ({ ...s, [arrKey]: [...s[arrKey], blank] }));
  const removeArrayRow = (arrKey, idx) =>
    setForm((s) => {
      const arr = s[arrKey].filter((_, i) => i !== idx);
      return { ...s, [arrKey]: arr.length ? arr : [""] };
    });
  const togglePreview = (key) => setPreviews((p) => ({ ...p, [key]: !p[key] }));

  // -------- validation --------
  const issues = useMemo(() => {
    const errs = [];
    if (!isAuthed) errs.push("You must be signed in.");
    if (blockedByAdminGuard) errs.push("You do not have permission to add stations.");
    if (!form.name.trim()) errs.push("Station name is required.");
    if (!form.game?.trim()) errs.push("Select a GTA game.");

    const slugOk = (v) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v);
    if (form.slug && !slugOk(form.slug.trim())) errs.push("Slug must be lowercase letters, numbers, and dashes only.");

    const urlLike = (v) => {
      if (!v?.trim()) return true;
      try { new URL(v.trim()); return true; } catch { return false; }
    };
    if (form.coverImage && !urlLike(form.coverImage)) errs.push("Cover Image URL is invalid.");
    if (form.spotifyUrl && !urlLike(form.spotifyUrl)) errs.push("Spotify URL is invalid.");
    if (form.youtubeUrl && !urlLike(form.youtubeUrl)) errs.push("YouTube URL is invalid.");
    if (form.appleMusicUrl && !urlLike(form.appleMusicUrl)) errs.push("Apple Music URL is invalid.");

    form.gallery.forEach((u, i) => {
      if (u.trim() && !urlLike(u)) errs.push(`Gallery URL #${i + 1} is invalid.`);
    });

    const anyGenre = form.genres.some((g) => g.trim());
    if (!anyGenre) errs.push("Add at least one genre.");

    return errs;
  }, [form, isAuthed, blockedByAdminGuard]);

  const canSubmit = issues.length === 0 && !saving;

  // -------- submit --------
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");

    try {
      const payload = {
        slug: form.slug.trim() || null,
        name: form.name.trim(),
        tagline: form.tagline.trim() || null,
        game: form.game || null,
        frequency: form.frequency.trim() || null,
        city: form.city.trim() || null,
        djs: form.djs.map((t) => t.trim()).filter(Boolean),
        genres: form.genres.map((t) => t.trim()).filter(Boolean),
        coverImage: form.coverImage.trim() || null,
        gallery: form.gallery.map((g) => g.trim()).filter(Boolean),
        spotifyUrl: form.spotifyUrl.trim() || null,
        youtubeUrl: form.youtubeUrl.trim() || null,
        appleMusicUrl: form.appleMusicUrl.trim() || null,
        description: form.description.trim(),
        trivia: form.trivia.trim(),
        tags: form.tagsCsv.split(",").map((t) => t.trim()).filter(Boolean),
        reactions: {},
        createdBy: user.uid,
        fanCreated: !isAdmin,
        status: form.status || "published",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...extraMeta,
      };

      const ref = await addDoc(collection(db, "radio_stations"), payload);
      onSaved?.(ref.id);
    } catch (err) {
      console.error(err);
      setError("Could not save station. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const loadSampleStation = () => {
    setForm((s) => ({
      ...s,
      ...SAMPLE_STATION,
      tagsCsv: SAMPLE_STATION.tags?.join(", ") || "",
    }));
  };

  return (
    <>
      {error && <div className="mt6-banner mt6-banner--error">{error}</div>}
      {issues.length > 0 && (
        <ul className="mt6-issues" role="alert">
          {issues.map((m, i) => (
            <li key={i}>• {m}</li>
          ))}
        </ul>
      )}

      <form onSubmit={onSubmit} className="mt6-form">
        {/* ---- Basics ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Basics</h4>

          <label className="mt6-label">Station Name</label>
          <input
            className="mt6-input"
            value={form.name}
            onChange={(e) => updateField("name", e.target.value)}
            placeholder="e.g., Radio Los Santos"
            required
            disabled={blockedByAdminGuard || saving}
          />

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Slug (URL-friendly)</label>
              <input
                className="mt6-input"
                value={form.slug}
                onChange={(e) => updateField("slug", e.target.value.toLowerCase())}
                placeholder="radio-los-santos"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">Frequency / Label</label>
              <input
                className="mt6-input"
                value={form.frequency}
                onChange={(e) => updateField("frequency", e.target.value)}
                placeholder="102.7 FM / Streaming"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">City / Region</label>
              <input
                className="mt6-input"
                list="mr-cities"
                value={form.city}
                onChange={(e) => updateField("city", e.target.value)}
                placeholder="Los Santos, Vice City, Liberty City…"
                disabled={blockedByAdminGuard || saving}
              />
              <datalist id="mr-cities">
                {DEFAULT_CITIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">GTA Game</label>
              <select
                className="mt6-input"
                value={form.game}
                onChange={(e) => updateField("game", e.target.value)}
                disabled={blockedByAdminGuard || saving}
              >
                {GAME_OPTIONS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="mt6-label">Tagline (optional)</label>
          <input
            className="mt6-input"
            value={form.tagline}
            onChange={(e) => updateField("tagline", e.target.value)}
            placeholder="Modern hip-hop and street anthems"
            disabled={blockedByAdminGuard || saving}
          />
        </div>

        {/* ---- People & Genres ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">DJs & Genres</h4>

          <label className="mt6-label">DJs</label>
          {form.djs.map((t, i) => (
            <div className="mt6-row" key={`dj-${i}`}>
              <input
                className="mt6-input"
                value={t}
                onChange={(e) => updateArrayField("djs", i, e.target.value)}
                placeholder={`DJ #${i + 1}`}
                disabled={blockedByAdminGuard || saving}
              />
              <button
                type="button"
                className="mt6-remove"
                onClick={() => removeArrayRow("djs", i)}
                disabled={blockedByAdminGuard || saving || form.djs.length <= 1}
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="mt6-add" onClick={() => addArrayRow("djs")} disabled={blockedByAdminGuard || saving}>
            ＋ Add DJ
          </button>

          <label className="mt6-label">Genres</label>
          {form.genres.map((t, i) => (
            <div className="mt6-row" key={`gn-${i}`}>
              <input
                className="mt6-input"
                value={t}
                onChange={(e) => updateArrayField("genres", i, e.target.value)}
                placeholder={`Genre #${i + 1}`}
                disabled={blockedByAdminGuard || saving}
              />
              <button
                type="button"
                className="mt6-remove"
                onClick={() => removeArrayRow("genres", i)}
                disabled={blockedByAdminGuard || saving || form.genres.length <= 1}
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="mt6-add" onClick={() => addArrayRow("genres")} disabled={blockedByAdminGuard || saving}>
            ＋ Add Genre
          </button>
        </div>

        {/* ---- Media ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Media</h4>

          <label className="mt6-label">Cover Image URL</label>
          <input
            className="mt6-input"
            value={form.coverImage}
            onChange={(e) => updateField("coverImage", e.target.value)}
            placeholder="https://…"
            disabled={blockedByAdminGuard || saving}
          />

          <label className="mt6-label">Gallery URLs</label>
          {form.gallery.map((u, i) => (
            <div className="mt6-row" key={`g-${i}`}>
              <input
                className="mt6-input"
                value={u}
                onChange={(e) => updateArrayField("gallery", i, e.target.value)}
                placeholder={`Image URL #${i + 1}`}
                disabled={blockedByAdminGuard || saving}
              />
              <button
                type="button"
                className="mt6-remove"
                onClick={() => removeArrayRow("gallery", i)}
                disabled={blockedByAdminGuard || saving || form.gallery.length <= 1}
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="mt6-add" onClick={() => addArrayRow("gallery")} disabled={blockedByAdminGuard || saving}>
            ＋ Add Image
          </button>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Spotify Embed / URL</label>
              <input
                className="mt6-input"
                value={form.spotifyUrl}
                onChange={(e) => updateField("spotifyUrl", e.target.value)}
                placeholder="https://open.spotify.com/embed/playlist/..."
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">YouTube URL (optional)</label>
              <input
                className="mt6-input"
                value={form.youtubeUrl}
                onChange={(e) => updateField("youtubeUrl", e.target.value)}
                placeholder="https://youtube.com/…"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">Apple Music URL (optional)</label>
              <input
                className="mt6-input"
                value={form.appleMusicUrl}
                onChange={(e) => updateField("appleMusicUrl", e.target.value)}
                placeholder="https://music.apple.com/…"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
          </div>

          {/* Live Spotify preview */}
          {form.spotifyUrl?.trim() ? (
            <div className="mt6-embed">
              <iframe
                title="spotify-embed-preview"
                src={form.spotifyUrl}
                width="100%"
                height="152"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{ borderRadius: 12 }}
              />
            </div>
          ) : null}
        </div>

        {/* ---- Content ---- */}
        {["description", "trivia"].map((key) => (
          <div className="mt6-section" key={key}>
            <h4 className="mt6-h4">{key === "description" ? "Description" : "Trivia / Notes"}</h4>
            <div className="mt6-segment">
              <button type="button" className={`mt6-seg-btn ${!previews[key] ? "is-active" : ""}`} onClick={() => togglePreview(key)}>
                Write
              </button>
              <button type="button" className={`mt6-seg-btn ${previews[key] ? "is-active" : ""}`} onClick={() => togglePreview(key)}>
                Preview
              </button>
            </div>
            {!previews[key] ? (
              <textarea
                className="mt6-input mt6-input--textarea"
                value={form[key]}
                onChange={(e) => updateField(key, e.target.value)}
                placeholder={key === "description" ? "What makes this station special?" : "Fun facts, guest DJs, history…"}
                disabled={blockedByAdminGuard || saving}
              />
            ) : (
              <div className="mt6-preview">
                {form[key].trim() ? <ReactMarkdown>{form[key]}</ReactMarkdown> : <span className="mt6-muted">Nothing to preview.</span>}
              </div>
            )}
          </div>
        ))}

        {/* ---- Tags & Status ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Tags & Status</h4>
          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Tags (CSV)</label>
              <input
                className="mt6-input"
                value={form.tagsCsv}
                onChange={(e) => updateField("tagsCsv", e.target.value)}
                placeholder="gta-v, los-santos, hip-hop"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">Status</label>
              <select
                className="mt6-input"
                value={form.status}
                onChange={(e) => updateField("status", e.target.value)}
                disabled={blockedByAdminGuard || saving}
              >
                {['published','pending','draft'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="mt6-maphelp">
              <small className="mt6-muted">Tip: Keep Spotify as an embed URL to stay copyright-safe.</small>
            </div>
          </div>
        </div>

        {/* ---- Actions ---- */}
        <div className="mt6-actions">
          <button type="submit" className="mt6-btn mt6-btn--primary" disabled={!canSubmit || saving}>
            {saving ? "Saving…" : "Save Station"}
          </button>

          <button type="button" className="mt6-btn mt6-btn--ghost" disabled={blockedByAdminGuard || saving} onClick={loadSampleStation} title="Fill the form with a ready-made example">
            Use Sample Station
          </button>

          {onCancel && (
            <button type="button" className="mt6-btn mt6-btn--ghost" disabled={saving} onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </>
  );
}
